// ===========================================
// AUTH SERVICE - Production Ready
// ===========================================

import api from './api';
import { sanitizeHubReturnPath } from '../lib/hubReturnPath';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  avatar?: string;
  phone?: string;
  timezone?: string;
  country?: string;
  bio?: string;
  jobTitle?: string;
  department?: string;
  organization: {
    id: string;
    name: string;
    slug?: string;
    subscriptionTier?: string;
  };
}

export interface LoginCredentials {
  email: string;
  password: string;
  captchaToken?: string;
}

export interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  organizationName: string;
  referralCode?: string;
  captchaToken?: string;
}

export interface AuthResponse {
  user: User;
  tokens: {
    accessToken: string;
    refreshToken: string;
  };
}

export const authService = {
  async login(credentials: LoginCredentials) {
    const response = await api.post<AuthResponse>('/api/auth/login', credentials);
    
    // Handle 2FA challenge - backend returns requiresTwoFactor instead of tokens
    if (response && (response as any).requiresTwoFactor) {
      return { success: true, requiresTwoFactor: true, challengeToken: (response as any).challengeToken };
    }
    
    // api.post now returns data directly (which is the data object from backend)
    if (response && response.user && response.tokens) {
      api.setToken(response.tokens.accessToken);
      localStorage.setItem('refresh_token', response.tokens.refreshToken);
      localStorage.setItem('user', JSON.stringify(response.user));
      return { success: true, data: { user: response.user, accessToken: response.tokens.accessToken, refreshToken: response.tokens.refreshToken } };
    }
    
    return { success: false, error: 'Login failed' };
  },

  async register(data: RegisterData) {
    const response = await api.post<AuthResponse>('/api/auth/register', data);
    
    if (response && response.user && response.tokens) {
      api.setToken(response.tokens.accessToken);
      localStorage.setItem('refresh_token', response.tokens.refreshToken);
      localStorage.setItem('user', JSON.stringify(response.user));
      return { success: true, data: { user: response.user, accessToken: response.tokens.accessToken, refreshToken: response.tokens.refreshToken } };
    }
    
    return { success: false, error: 'Registration failed' };
  },

  async verifyTwoFactor(challengeToken: string, token: string, backupCode?: string) {
    const response = await api.post<AuthResponse>('/api/auth/login/2fa', {
      challengeToken,
      ...(backupCode ? { backupCode } : { token }),
    });
    
    if (response && response.user && response.tokens) {
      api.setToken(response.tokens.accessToken);
      localStorage.setItem('refresh_token', response.tokens.refreshToken);
      localStorage.setItem('user', JSON.stringify(response.user));
      return { success: true, data: { user: response.user, accessToken: response.tokens.accessToken, refreshToken: response.tokens.refreshToken } };
    }
    
    return { success: false, error: 'Verification failed' };
  },

  async logout() {
    try {
      await api.post('/api/auth/logout');
    } finally {
      api.setToken(null);
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
    }
  },

  async getProfile() {
    const response = await api.get<{ user: User }>('/api/auth/me');
    return response?.user || null;
  },

  async forgotPassword(email: string) {
    return api.post('/api/auth/forgot-password', { email });
  },

  async resetPassword(token: string, password: string) {
    return api.post('/api/auth/reset-password', { token, password });
  },

  getCurrentUser(): User | null {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch {
        return null;
      }
    }
    return null;
  },

  isAuthenticated(): boolean {
    return !!api.getToken();
  },

  async updateProfile(data: Partial<User>): Promise<User> {
    const response = await api.patch<{ user: User }>('/api/auth/profile', data);
    return response?.user || data as User;
  },

  // ===========================================
  // PLATFORM HUB SSO METHODS
  // ===========================================

  /**
   * Get Platform Hub login URL (app=tripnovac-sales&returnPath=/…)
   */
  async getPlatformHubLoginUrl(returnPath?: string): Promise<string> {
    const safePath = sanitizeHubReturnPath(
      returnPath ||
        `${window.location.pathname}${window.location.search}` ||
        '/dashboard',
      '/dashboard'
    );
    const response = await api.get<{ loginUrl: string }>(
      `/api/auth/login-url?returnPath=${encodeURIComponent(safePath)}`
    );
    return (response as any)?.loginUrl || (response as any)?.data?.loginUrl || '';
  },

  /**
   * Redirect to Platform Hub for SSO login (never bare /login)
   */
  async loginWithPlatformHub(returnPath?: string) {
    const loginUrl = await this.getPlatformHubLoginUrl(returnPath);
    if (loginUrl) {
      window.location.href = loginUrl;
    } else {
      throw new Error('Platform Hub login URL was empty');
    }
  },

  /**
   * Get Google OAuth URL (Sales Google client → /api/auth/google)
   */
  async getGoogleOAuthUrl(): Promise<string> {
    try {
      const response = await api.get<{ loginUrl?: string; data?: { loginUrl?: string } }>('/api/auth/google-url');
      return (response as any)?.loginUrl || (response as any)?.data?.loginUrl || '';
    } catch (err) {
      console.error('Failed to get Google OAuth URL', err);
      throw err;
    }
  },

  /**
   * Redirect to Google OAuth (full-page; not an iframe)
   */
  async loginWithGoogle() {
    try {
      const loginUrl = await this.getGoogleOAuthUrl();
      if (!loginUrl) {
        throw new Error('Google sign-in is not available right now');
      }
      window.location.href = loginUrl;
    } catch (err) {
      console.error(err);
      alert(err instanceof Error ? err.message : 'Google sign-in failed. Please try again or use email login.');
    }
  },

  /**
   * Get Apple Sign In URL (APPLE_SERVICE_ID — not Cognito)
   */
  async getAppleOAuthUrl(): Promise<string> {
    try {
      const response = await api.get<{ loginUrl?: string; data?: { loginUrl?: string } }>('/api/auth/apple-url');
      return (response as any)?.loginUrl || (response as any)?.data?.loginUrl || '';
    } catch (err) {
      console.error('Failed to get Apple OAuth URL', err);
      throw err;
    }
  },

  /**
   * Redirect to Apple Sign In
   */
  async loginWithApple() {
    try {
      const loginUrl = await this.getAppleOAuthUrl();
      if (!loginUrl) {
        throw new Error('Apple sign-in is not available right now');
      }
      window.location.href = loginUrl;
    } catch (err) {
      console.error(err);
      alert(err instanceof Error ? err.message : 'Apple sign-in failed. Please try again or use email login.');
    }
  },

  /**
   * Validate SSO token from Platform Hub
   */
  async validateSSOToken(accessToken: string): Promise<boolean> {
    try {
      const response = await api.post<{ valid: boolean }>('/api/auth/validate', { accessToken });
      return response?.valid || false;
    } catch {
      return false;
    }
  },

  /**
   * Refresh access token using refresh token
   */
  async refreshToken(refreshToken?: string): Promise<{ accessToken: string; refreshToken: string } | null> {
    try {
      const token = refreshToken || localStorage.getItem('refresh_token');
      if (!token) return null;

      const response = await api.post<{ accessToken: string; refreshToken: string }>(
        '/api/auth/refresh',
        { refreshToken: token }
      );

      if (response?.accessToken) {
        api.setToken(response.accessToken);
        if (response.refreshToken) {
          localStorage.setItem('refresh_token', response.refreshToken);
        }
        return response;
      }

      return null;
    } catch {
      return null;
    }
  },

  /**
   * Logout from Platform Hub SSO
   */
  async logoutSSO() {
    try {
      const response = await api.post<{ cognitoLogoutUrl?: string }>('/api/sso/logout');
      
      // Clear local tokens
      api.setToken(null);
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('sessionId');
      localStorage.removeItem('user');

      // Redirect to Cognito logout if provided (clears Cognito session)
      if (response?.cognitoLogoutUrl) {
        window.location.href = response.cognitoLogoutUrl;
      } else {
        window.location.href = '/login';
      }
    } catch (error) {
      // Fallback: clear local storage and redirect
      api.setToken(null);
      localStorage.clear();
      window.location.href = '/login';
    }
  },
};

export default authService;

